import { createContext, type PropsWithChildren, type ReactNode, useContext, useMemo } from 'react'
import { parseAsStringLiteral, useQueryState, type UseQueryStateReturn } from 'nuqs'
import { parseAsSearchQuery } from '@util'
import { isDefined, isEmpty, searchIncludes, unique, type Preference } from '@terros/common'
import { PREFERENCE_DEFINITIONS, PREFERENCE_GROUPS, type PreferenceGroup } from '@front/model'

export type PreferenceGroupOrAll = PreferenceGroup | 'all'

type PreferenceContext = {
  isAllowedUpdate: boolean
  filter: string
  setFilter: (filter: string) => void
  selectedGroup: PreferenceGroupOrAll
  setSelectedGroup: (group: PreferenceGroupOrAll) => void
}

export const PreferenceContext = createContext<PreferenceContext | null>(null)

export const usePreferenceContext = (): PreferenceContext => {
  const context = useContext(PreferenceContext)
  if (!context) {
    throw new Error('usePreferenceContext must be used within a PreferenceContextProvider')
  }
  return context
}

export const PreferenceFormProvider = ({
  children,
  allowUpdate,
  defaultGroup,
}: PropsWithChildren<{ allowUpdate: boolean; defaultGroup?: PreferenceGroupOrAll }>): ReactNode => {
  const [filter, setFilter] = useFilterQueryState()
  const availableGroups = useAvailablePreferenceGroups(filter)
  const [selectedGroup, setSelectedGroup] = useSelectedGroupQueryState(availableGroups, defaultGroup)
  const contextValue = useMemo(
    () => ({ isAllowedUpdate: allowUpdate, filter, setFilter, selectedGroup, setSelectedGroup }),
    [allowUpdate, filter, setFilter, selectedGroup, setSelectedGroup]
  )
  return <PreferenceContext.Provider value={contextValue}>{children}</PreferenceContext.Provider>
}

export const useFilterQueryState = (): UseQueryStateReturn<string, string> => {
  return useQueryState('filter', parseAsSearchQuery.withDefault(''))
}

export const useAvailablePreferenceGroups = (filter: string): readonly PreferenceGroup[] => {
  return useMemo(() => {
    if (isEmpty(filter)) return PREFERENCE_GROUPS
    return unique(
      Object.entries(PREFERENCE_DEFINITIONS)
        .filter(([key, definition]) =>
          searchIncludes([key, definition.label, definition.description, definition.group], filter)
        )
        .map(([_, definition]) => definition.group)
    )
  }, [filter])
}

export const useSelectedGroupQueryState = (
  availableGroups: readonly PreferenceGroup[],
  defaultGroup?: PreferenceGroupOrAll
): UseQueryStateReturn<PreferenceGroupOrAll, PreferenceGroupOrAll> => {
  const [_selectedGroup, setSelectedGroup] = useQueryState<PreferenceGroupOrAll>(
    'selectedGroup',
    parseAsStringLiteral([...PREFERENCE_GROUPS, 'all'] as const).withDefault(defaultGroup ?? PREFERENCE_GROUPS[0])
  )
  const currentGroup = useMemo(() => {
    if (_selectedGroup === 'all') return 'all'
    if (isDefined(_selectedGroup) && availableGroups.some((group) => group === _selectedGroup)) return _selectedGroup
    return defaultGroup ?? availableGroups[0]
  }, [_selectedGroup, availableGroups, defaultGroup])
  return [currentGroup, setSelectedGroup]
}

export const useMatchesPreferenceFilter = (preference: Preference): boolean => {
  const { filter, selectedGroup } = usePreferenceContext()

  return useMemo(() => {
    const definition = PREFERENCE_DEFINITIONS[preference]
    if (selectedGroup !== 'all') {
      if (selectedGroup !== definition.group) return false
    }
    if (isEmpty(filter)) return true
    const searchValues = [preference, definition.label, definition.description, definition.group]
    return searchIncludes(searchValues, filter)
  }, [filter, preference, selectedGroup])
}
